import { makeAutoObservable } from "mobx";
import type { EpisodesApi } from "@/api/episodes";
import type { PodcastsApi } from "@/api/podcasts";
import type { TrackingApi } from "@/api/tracking";
import { localGet } from "@/core/helpers";
import { Resource } from "./Resource";

/** The ids kept in local storage under `key`, newest first. Anything else there is no history. */
const storedIds = async (key: string): Promise<number[]> => {
    const ids: unknown = await localGet(key);
    return Array.isArray(ids) ? ids.filter((id): id is number => typeof id === "number") : [];
};

/**
 * The visitor of this tab: a session id that tags the events the app reports, and the episodes and
 * podcasts they listened to most recently (kept on the device, looked up here).
 */
export class UserStore {
    userId = "";

    /** Keyed by the ids as "3,1,2", so the same history is fetched once */
    private readonly episodeHistory: Resource<Episode[], string>;
    private readonly podcastHistory: Resource<Podcast[], string>;

    constructor(
        episodesApi: EpisodesApi,
        podcastsApi: PodcastsApi,
        private readonly tracking: TrackingApi
    ) {
        this.episodeHistory = new Resource((ids) => episodesApi.byIds(ids.split(",").map(Number)));
        this.podcastHistory = new Resource((ids) => podcastsApi.byIds(ids.split(",").map(Number)));
        makeAutoObservable<UserStore, "tracking" | "episodeHistory" | "podcastHistory">(this, {
            tracking: false,
            episodeHistory: false,
            podcastHistory: false,
        });

        this.userId = sessionId();
        window.clientId = this.userId;
    }

    get lastEpisodes(): Episode[] {
        return this.episodeHistory.data ?? [];
    }

    get lastPodcasts(): Podcast[] {
        return this.podcastHistory.data ?? [];
    }

    async suggest(url: string) {
        if (this.userId.length === 0) return;
        await this.tracking.suggest(this.userId, url);
    }

    /** Looks up the episodes in the history on this device. Does nothing again until the history changes. */
    async latestEpisodes() {
        const ids = await storedIds("hp_episode_history");
        if (ids.length > 0) {
            await this.episodeHistory.load(ids.join(","));
        }
    }

    async latestPodcasts() {
        const ids = await storedIds("hp_pod_history");
        if (ids.length > 0) {
            await this.podcastHistory.load(ids.join(","));
        }
    }

    async trackUserEvents(action: string, payload: unknown = {}) {
        await this.tracking.userAction(this.userId, action, payload);
    }
}

/** One id per tab: the same through reloads, new in a new tab */
function sessionId(): string {
    let existing = sessionStorage.getItem("hp_session_id");
    if (!existing && globalThis.crypto?.randomUUID) {
        try {
            existing = globalThis.crypto.randomUUID();
            sessionStorage.setItem("hp_session_id", existing);
        } catch (e) {
            console.error(e);
        }
    }

    if (!existing) {
        existing = Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
        sessionStorage.setItem("hp_session_id", existing);
    }
    return existing;
}
